import {
  Box,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Grid,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';

interface QualityAssessment {
  overallScore: number;
  relevanceScore: number;
  clarityScore: number;
  innovationScore: number;
  feasibilityScore: number;
  strengthAreas: string[];
  improvementAreas: string[];
}

interface QualityDetailsProps {
  data: QualityAssessment;
}

export default function QualityDetails({ data }: QualityDetailsProps) {
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const dimensions = [
    { label: 'Relevance', score: data.relevanceScore },
    { label: 'Clarity', score: data.clarityScore },
    { label: 'Innovation', score: data.innovationScore },
    { label: 'Feasibility', score: data.feasibilityScore },
  ];

  return (
    <Stack spacing={3}>
      {/* Quality Dimensions */}
      <Box>
        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
          Quality Dimensions:
        </Typography>
        <Grid container spacing={2}>
          {dimensions.map((dimension, idx) => (
            <Grid key={idx} size={{ xs: 12, sm: 6, md: 3 }}>
              <Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">{dimension.label}</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {dimension.score}/100
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={dimension.score}
                  color={getScoreColor(dimension.score) as 'success' | 'warning' | 'error'}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Strength Areas */}
      {data.strengthAreas.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            Strength Areas:
          </Typography>
          <List dense>
            {data.strengthAreas.map((area, idx) => (
              <ListItem key={idx} disablePadding>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={area} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Areas for Improvement */}
      {data.improvementAreas.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            Areas for Improvement:
          </Typography>
          <List dense>
            {data.improvementAreas.map((area, idx) => (
              <ListItem key={idx} disablePadding>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <TipsAndUpdatesIcon color="warning" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={area} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Stack>
  );
}
